import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { X, AlertCircle, Grid, List } from 'lucide-react';
import Modal from '@/components/common/Modal/Modal';
import Button from '@/components/common/Button/Button';
import { useGames } from '@/Context/GameContext';
import SearchBar from './components/SearchBar';
import Filters from './components/Filters';
import GameGrid from './components/GameGrid';
import styles from './GlobalSearchModal.module.css';

/**
 * GlobalSearchModal Component
 * Comprehensive game search modal with filters, sorting, and grid/list views
 * Opens via route: /modal?mode=search
 * Full screen on mobile, modal on desktop
 */
const GlobalSearchModal = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const mode = searchParams.get('mode');

  const {
    searchResults,
    totalCount,
    hasMorePages,
    isLoading,
    error,
    currentQuery,
    selectedVendors,
    searchGames,
    loadMoreGames,
    handleGameLaunch,
    clearError
  } = useGames();

  const [isOpen, setIsOpen] = useState(false);
  const [searchInput, setSearchInput] = useState('');
  const [viewMode, setViewMode] = useState('grid');
  const searchTimeoutRef = useRef(null);
  const hasInitializedRef = useRef(false);

  // Handle route-based modal opening
  useEffect(() => {
    const pathname = location.pathname;
    const shouldOpen = pathname === '/modal' && mode === 'search';

    if (shouldOpen) {
      setIsOpen(true);
      // Clear any previous errors when opening modal
      if (error) {
        clearError();
      }
      // Initialize search only once when modal opens (not on home page)
      // This prevents API calls when user is just browsing home page
      // searchGames will use pre-fetched React Query cache if available
      if (searchResults.length === 0 && !currentQuery && !isLoading && !hasInitializedRef.current) {
        hasInitializedRef.current = true;
        // This will use pre-fetched cache from React Query (no API call if cache exists)
        searchGames('', selectedVendors || []);
      }
    } else {
      setIsOpen(false);
      // Reset initialization flag when modal closes
      if (!shouldOpen) {
        hasInitializedRef.current = false;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, mode, selectedVendors, searchResults.length, currentQuery, isLoading, error, clearError]);

  const handleClose = () => {
    setIsOpen(false);
    // Navigate back or to home when modal closes
    if (location.pathname !== '/') {
      navigate('/');
    } else {
      navigate(-1);
    }
  };

  // Handle search input with debounce - memoized to prevent recreation
  const handleSearchInput = useCallback((e) => {
    const value = e.target.value;
    setSearchInput(value);

    // Clear error when user starts typing
    if (error) {
      clearError();
    }

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      searchGames(value, selectedVendors);
    }, 300);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedVendors, error, clearError]);

  // Cleanup
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  // Reset search input when query changes externally
  useEffect(() => {
    if (currentQuery !== searchInput) {
      setSearchInput(currentQuery);
    }
  }, [currentQuery]);

  // Clear error when search results are successfully loaded
  useEffect(() => {
    if (searchResults.length > 0 && error) {
      clearError();
    }
  }, [searchResults.length, error, clearError]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      size="xl"
      title="Game Library"
      showCloseButton={true}
      className={styles.modal}
    >
      <div style={{ marginTop: '10px' }} className={styles.modalBody}>
        {/* Header Section */}
        <div className={styles.header}>
          <div className={styles.searchAndSort}>
            <SearchBar
              value={searchInput}
              onChange={handleSearchInput}
              placeholder="Search games..."
              autoFocus={true}
            />
            <Filters
              viewMode={viewMode}
              onViewModeChange={setViewMode}
              showViewToggle={false}
            />
          </div>
        </div>

        {/* Stats Bar with View Toggle */}
        <div className={styles.statsAndViewRow}>
          {searchResults.length > 0 && (
            <div className={styles.statsBar}>
              <span>
                Showing {searchResults.length} of {totalCount} games
                {selectedVendors.length > 0 && ` from ${selectedVendors.length} provider(s)`}
              </span>
            </div>
          )}
          <div className={styles.viewToggleContainer}>
            <Button
              variant={viewMode === 'grid' ? 'primary' : 'secondary'}
              size="sm"
              onClick={() => setViewMode('grid')}
              title="Grid View"
              className={styles.viewButton}
            >
              <Grid size={16} />
            </Button>
            <Button
              variant={viewMode === 'list' ? 'primary' : 'secondary'}
              size="sm"
              onClick={() => setViewMode('list')}
              title="List View"
              className={styles.viewButton}
            >
              <List size={16} />
            </Button>
          </div>
        </div>

        {/* Error Banner - Only show when not loading and there's an error */}
        {error && !isLoading && (
          <div className={styles.errorBanner}>
            <AlertCircle size={16} />
            <span>{error}</span>
            <button className={styles.dismissButton} onClick={clearError}>
              <X size={16} />
            </button>
          </div>
        )}

        {/* Content */}
        <div className={styles.content}>
          <GameGrid
            games={searchResults}
            viewMode={viewMode}
            onGameClick={(game) => handleGameLaunch(game, navigate)}
            isLoading={isLoading}
          />

          {/* Load More Button */}
          {hasMorePages && searchResults.length > 0 && (
            <div style={{ display: 'flex', justifyContent: 'center', width: '100%' }}>

              <Button
                variant="primary"
                size="lg"
                fullWidth
                onClick={loadMoreGames}
                disabled={isLoading}
                className={styles.loadMoreButton}
              >
                {isLoading ? 'Loading...' : 'Load More Games'}
              </Button>
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default GlobalSearchModal;
